/**
 * QR camera scanning that actually works on a phone.
 *
 * WHY THIS EXISTS: the previous implementation required `window.BarcodeDetector`,
 * which only exists in Chrome/Android. iOS Safari — the browser on every iPhone —
 * has no such API, so `startCamera()` bailed with "no camera QR support" and the
 * counter could never scan a guest's pass on an iPhone. A scan feature that only
 * works on one mobile platform is not a scan feature.
 *
 * The fix is to stop assuming a decoder: use BarcodeDetector when it is present
 * (fast, hardware-accelerated) and otherwise decode frames with jsQR, which is
 * pure JavaScript and runs anywhere.
 *
 * iOS ALSO requires care with the stream:
 *  - video must be `playsinline` and `muted`, or iOS takes it fullscreen and never
 *    paints into the element;
 *  - getUserMedia must be called in response to a user gesture;
 *  - the track is stopped explicitly on teardown, or the camera indicator stays
 *    lit after the sheet closes.
 */
import jsQR from 'jsqr'

export interface CameraScanHandle {
  stop: () => void
}

interface StartOptions {
  video: HTMLVideoElement
  /** Called once, with the first decoded value. The camera is stopped first. */
  onResult: (value: string) => void
  /** Called if the camera cannot be used; the caller should offer manual entry. */
  onError: (message: string) => void
}

export function canUseCamera(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    typeof window !== 'undefined' &&
    typeof HTMLVideoElement !== 'undefined'
  )
}

/**
 * Start scanning. Resolves once the stream is running (or has failed and onError
 * has been called). The returned handle must be invoked on cleanup.
 */
export async function startQrCamera(opts: StartOptions): Promise<CameraScanHandle | null> {
  const { video, onResult, onError } = opts

  if (!canUseCamera()) {
    onError('This browser cannot open the camera. Type the pass code instead.')
    return null
  }

  let stream: MediaStream
  try {
    // facingMode 'environment' = the rear camera, which is what you point at a
    // guest's phone. `ideal` rather than `exact`: a laptop or a phone with one
    // camera should still work instead of throwing OverconstrainedError.
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
      audio: false,
    })
  } catch (e) {
    const name = (e as { name?: string })?.name ?? ''
    onError(
      name === 'NotAllowedError'
        ? 'Camera permission was refused. Allow camera access, or type the pass code.'
        : name === 'NotFoundError'
          ? 'No camera found on this device. Type the pass code instead.'
          : 'The camera could not be opened. Type the pass code instead.',
    )
    return null
  }

  // iOS: these two must be set as properties, not only as JSX attributes.
  video.playsInline = true
  video.muted = true
  video.srcObject = stream

  try {
    await video.play()
  } catch {
    // Autoplay can still be refused; the user gesture that started this should
    // prevent it, but if not the frames will not advance and we stop cleanly.
    stream.getTracks().forEach((t) => t.stop())
    onError('The camera preview could not start. Type the pass code instead.')
    return null
  }

  let stopped = false
  let rafId: number | undefined

  function stop() {
    stopped = true
    if (rafId !== undefined) cancelAnimationFrame(rafId)
    rafId = undefined
    stream.getTracks().forEach((t) => t.stop())
    if (video.srcObject === stream) video.srcObject = null
  }

  // Canvas is created once and reused; allocating one per frame is what makes
  // naive implementations crawl on a phone.
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d', { willReadFrequently: true })

  const Detector = (window as unknown as { BarcodeDetector?: new (o: { formats: string[] }) => { detect: (v: HTMLVideoElement) => Promise<Array<{ rawValue: string }>> } })
    .BarcodeDetector
  const fastDetector = Detector ? new Detector({ formats: ['qr_code'] }) : null

  async function tick() {
    if (stopped) return

    // Nothing to read until the element has real dimensions.
    if (video.readyState >= 2 && video.videoWidth > 0) {
      let value: string | null = null

      if (fastDetector) {
        try {
          const found = await fastDetector.detect(video)
          if (found?.length) value = found[0]?.rawValue ?? null
        } catch {
          /* not ready yet — fall through to jsQR */
        }
      }

      if (!value && ctx) {
        // Sample at a modest size: a 1280-wide frame downscaled to 480 is plenty
        // to decode a QR and costs a fraction of the per-frame work.
        const w = 480
        const h = Math.round((video.videoHeight / video.videoWidth) * w) || 480
        if (canvas.width !== w || canvas.height !== h) {
          canvas.width = w
          canvas.height = h
        }
        ctx.drawImage(video, 0, 0, w, h)
        const image = ctx.getImageData(0, 0, w, h)
        const decoded = jsQR(image.data, image.width, image.height, {
          inversionAttempts: 'dontInvert',
        })
        if (decoded?.data) value = decoded.data
      }

      if (value) {
        stop()
        onResult(value)
        return
      }
    }

    rafId = requestAnimationFrame(() => void tick())
  }

  void tick()
  return { stop }
}
